/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { Box, Button, Heading, Icon, Stack, DividerNew as Divider } from 'sarsaparilla';

import VehicleItem from './VehicleItem';
import {
    VEHICLES_PROP_PATH,
    VEHICLES_LICENSE_PLATE_PROP_PATH,
    VEHICLES_STATE_PROP_PATH,
    VEHICLES_MAKE_PROP_PATH,
    VEHICLES_TYPE_PROP_PATH,
} from '../constants/propPaths';

const defaultDataBindingMap = {
    [VEHICLES_PROP_PATH]: 'extra_fields.vehicles_information',
    [VEHICLES_LICENSE_PLATE_PROP_PATH]: 'license_plate_number',
    [VEHICLES_STATE_PROP_PATH]: 'state',
    [VEHICLES_MAKE_PROP_PATH]: 'make',
    [VEHICLES_TYPE_PROP_PATH]: 'type',
};

// Currently we don't need the issuanceShape as all props are retrieved to propPath
// if need later on, replace the '_' paramater
export default function VehiclesDetailHOC(_, dataBindingsMap = defaultDataBindingMap) {
    const dm = { ...defaultDataBindingMap, ...dataBindingsMap };

    const vehiclesPropPath = () => `${dm[VEHICLES_PROP_PATH]}`;
    const vehicleLicensePlatePropPath = (idx) =>
        `${dm[VEHICLES_PROP_PATH]}[${idx}].${dm[VEHICLES_LICENSE_PLATE_PROP_PATH]}`;
    const vehicleStatePropPath = (idx) =>
        `${dm[VEHICLES_PROP_PATH]}[${idx}].${dm[VEHICLES_STATE_PROP_PATH]}`;
    const vehicleMakePropPath = (idx) =>
        `${dm[VEHICLES_PROP_PATH]}[${idx}].${dm[VEHICLES_MAKE_PROP_PATH]}`;
    const vehicleTypePropPath = (idx) =>
        `${dm[VEHICLES_PROP_PATH]}[${idx}].${dm[VEHICLES_TYPE_PROP_PATH]}`;

    class VehiclesDetail extends React.PureComponent {
        static propTypes = {
            isDescriptionRequired: PropTypes.bool,
            isLicensePlateRequired: PropTypes.bool,
            isTypeRequired: PropTypes.bool,
            isStateRequired: PropTypes.bool,
            isReadOnly: PropTypes.bool,
            minVehicles: PropTypes.number,
            maxVehicles: PropTypes.number,
            pActions: PropTypes.object,
            reservation: PropTypes.object,
            shouldValidateOnBEOnly: PropTypes.bool,
            validation: PropTypes.object,
            vehicleTypes: PropTypes.array,
        };

        componentDidMount() {
            if (!isNil(get(this.props, 'reservation.id'))) {
                const vehicles = this.getVehicles();
                this.props.pActions.updatePendingReservation(
                    get(this.props, 'reservation.id'),
                    vehiclesPropPath(),
                    vehicles
                );
            }
        }

        onFormValidate(isInValid, error, path) {
            this.props.pActions.formValidationChange(
                `${this.props.reservation.id}_${path}`,
                isInValid ? error : null
            );
        }

        getVehicles = () => {
            const defaultVehiclesToUse = this.props?.minVehicles
                ? Array.from({ length: this.props.minVehicles }).map(() => ({}))
                : [];
            return (
                get(this.props.reservation, vehiclesPropPath()) || defaultVehiclesToUse
            );
        };

        getInputsValidation(idx) {
            const { reservation, validation, shouldValidateOnBEOnly } = this.props;

            if (shouldValidateOnBEOnly) {
                return {
                    isLicensePlateInvalid: get(
                        validation,
                        vehicleLicensePlatePropPath(idx)
                    ),
                    isMakeInvalid: get(validation, vehicleMakePropPath(idx)),
                    isTypeInvalid: get(validation, vehicleTypePropPath(idx)),
                };
            }

            if (!validation.displayValidationError) return {};

            const LicensePlatePath = `${reservation.id}_${vehicleLicensePlatePropPath(idx)}`;
            const MakePath = `${reservation.id}_${vehicleMakePropPath(idx)}`;
            const TypePath = `${reservation.id}_${vehicleTypePropPath(idx)}`;

            return {
                isLicensePlateInvalid: Boolean(get(validation, LicensePlatePath)),
                isMakeInvalid: Boolean(get(validation, MakePath)),
                isTypeInvalid: Boolean(get(validation, TypePath)),
            };
        }

        handleLicensePlateValidated = (isInValid, error, index) =>
            this.onFormValidate(isInValid, error, vehicleLicensePlatePropPath(index));

        handleMakeValidated = (isInValid, error, index) =>
            this.onFormValidate(isInValid, error, vehicleMakePropPath(index));

        handleTypeValidated = (isInValid, error, index) =>
            this.onFormValidate(isInValid, error, vehicleTypePropPath(index));

        handleAddVehicle = () => {
            const cloneOfVehicles = cloneDeep(this.getVehicles());
            cloneOfVehicles.push({});
            this.props.pActions.updatePendingReservation(
                get(this.props, 'reservation.id'),
                vehiclesPropPath(),
                cloneOfVehicles
            );
        };

        handleRemoveVehicle = (idx) => {
            // remove the specific popped vehicle
            const cloneOfVehicles = cloneDeep(this.getVehicles());
            cloneOfVehicles.splice(idx, 1);

            // remove all validation errors for vehicles[idx]
            if (this.props.shouldValidateOnBEOnly) {
                this.props.pActions.formValidationChange(vehicleMakePropPath(idx), null);
                this.props.pActions.formValidationChange(
                    vehicleLicensePlatePropPath(idx),
                    null
                );
                this.props.pActions.formValidationChange(vehicleStatePropPath(idx), null);
            }
            return this.props.pActions.updatePendingReservation(
                get(this.props, 'reservation.id'),
                vehiclesPropPath(),
                cloneOfVehicles
            );
        };

        handleVehicleTypeChanged = (idx, type) => {
            if (this.props.shouldValidateOnBEOnly) {
                this.props.pActions.formValidationChange(vehicleTypePropPath(idx), null);
            }

            return this.props.pActions.updatePendingReservation(
                get(this.props, 'reservation.id'),
                vehicleTypePropPath(idx),
                type
            );
        };

        handleVehicleMakeChanged = (idx, make) => {
            if (this.props.shouldValidateOnBEOnly) {
                this.props.pActions.formValidationChange(vehicleMakePropPath(idx), null);
            }

            return this.props.pActions.updatePendingReservation(
                get(this.props, 'reservation.id'),
                vehicleMakePropPath(idx),
                make
            );
        };

        handleVehicleLicensePlateChanged = (idx, licensePlate) => {
            if (this.props.shouldValidateOnBEOnly) {
                this.props.pActions.formValidationChange(
                    vehicleLicensePlatePropPath(idx),
                    null
                );
            }

            return this.props.pActions.updatePendingReservation(
                get(this.props, 'reservation.id'),
                vehicleLicensePlatePropPath(idx),
                licensePlate
            );
        };

        handleVehicleStateChanged = (idx, vehicleState) => {
            if (this.props.shouldValidateOnBEOnly) {
                this.props.pActions.formValidationChange(vehicleStatePropPath(idx), null);
            }

            return this.props.pActions.updatePendingReservation(
                get(this.props, 'reservation.id'),
                vehicleStatePropPath(idx),
                vehicleState
            );
        };

        render() {
            const {
                isLicensePlateRequired,
                isDescriptionRequired,
                isStateRequired,
                isTypeRequired,
                isReadOnly,
                maxVehicles,
                reservation,
                vehicleTypes,
            } = this.props;

            const getVehicleLicensePlate = (idx) =>
                get(reservation, vehicleLicensePlatePropPath(idx), '');

            const getVehicleMake = (idx) =>
                get(reservation, vehicleMakePropPath(idx), '');

            const getVehicleState = (idx) =>
                get(reservation, vehicleStatePropPath(idx), '');

            const getVehicleType = (idx) =>
                get(reservation, vehicleTypePropPath(idx), '');

            const vehicles = this.getVehicles();

            return (
                <div className="per-form-group-vehicle per-section-group mt-3">
                    <Heading headingLevel={2} appearance="h5" className="mb-2">
                        Vehicle Information
                    </Heading>
                    <p className="mb-2">
                        Please provide information about your vehicle. For rental
                        vehicles, add &quot;rental&quot; in the required fields; if you
                        will not have a vehicle, add &quot;no vehicle.&quot;
                    </p>
                    <div className="per-total-count-wrapper mb-2">
                        <span className="per-total-count">
                            Total Vehicles: <b>{`${vehicles.length} / ${maxVehicles}`}</b>
                        </span>
                    </div>

                    {vehicles.length < maxVehicles && (
                        <Box>
                            <Button
                                appearance="link"
                                onClick={this.handleAddVehicle}
                                iconBeforeElement={<Icon iconName="add-circle" />}
                            >
                                Add Vehicle
                            </Button>
                        </Box>
                    )}

                    {vehicles.length > 0 && (
                        <Box background="gray-2" padding="md">
                            <Stack>
                                {vehicles.map((vehicle, index) => {
                                    const {
                                        isLicensePlateInvalid,
                                        isMakeInvalid,
                                        isTypeInvalid,
                                    } = this.getInputsValidation(index);
                                    return (
                                        <>
                                            <VehicleItem
                                                key={`vehicle-row-${index}`}
                                                index={index}
                                                isReadOnly={isReadOnly}
                                                make={getVehicleMake(index)}
                                                isMakeRequired={isDescriptionRequired}
                                                isMakeInvalid={isMakeInvalid}
                                                onMakeChanged={
                                                    this.handleVehicleMakeChanged
                                                }
                                                onMakeValidated={this.handleMakeValidated}
                                                type={getVehicleType(index)}
                                                typesOptions={vehicleTypes}
                                                isTypeRequired={isTypeRequired}
                                                isTypeInvalid={isTypeInvalid}
                                                onTypeChanged={
                                                    this.handleVehicleTypeChanged
                                                }
                                                onTypeValidated={this.handleTypeValidated}
                                                licensePlate={getVehicleLicensePlate(
                                                    index
                                                )}
                                                isLicensePlateRequired={
                                                    isLicensePlateRequired
                                                }
                                                isLicensePlateInvalid={
                                                    isLicensePlateInvalid
                                                }
                                                onLicensePlateValidated={
                                                    this.handleLicensePlateValidated
                                                }
                                                onLicensePlateChanged={
                                                    this.handleVehicleLicensePlateChanged
                                                }
                                                onStateChanged={
                                                    this.handleVehicleStateChanged
                                                }
                                                isStateRequired={isStateRequired}
                                                state={getVehicleState(index)}
                                                onRemoveVehicle={this.handleRemoveVehicle}
                                            />
                                            {index < vehicles.length - 1 && (
                                                <Divider
                                                    key={`vehicle-row-${index}-divider`}
                                                />
                                            )}
                                        </>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}
                </div>
            );
        }
    }

    return VehiclesDetail;
}
